import { flip } from "@floating-ui/dom";
import {
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  autoUpdate,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useListNavigation,
} from "@floating-ui/react";
import { isEmpty } from "lodash";
import {
  ChangeEvent,
  ComponentType,
  KeyboardEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

import { Box } from "@design-system/Box/Box";
import { Button } from "@design-system/Button/Button";
import { Card } from "@design-system/Card/Card";
import { Icon } from "@design-system/Icon/Icon";
import { Input } from "@design-system/Input/Input";
import { InputBox } from "@design-system/InputBox/InputBox";
import { ScrollBox } from "@design-system/ScrollBox/ScrollBox";
import { Text } from "@design-system/Text/Text";

import { SelectContext } from "./SelectContext";
import { Option } from "./UI/options/Option/Option";
import { OptionContainer } from "./UI/options/OptionContainer/OptionContainer";
import { TagValue } from "./UI/values/TagValue/TagValue";
import { Value } from "./UI/values/Value/Value";

import classes from "./Select.module.css";

type SingleSelectValue = string;
type MultiSelectValue = string[];
type SelectValue = SingleSelectValue | MultiSelectValue;

export type Option = {
  id: string;
  label: string;
};

export type ValueComponentProps = {
  options: Option[];
  value: string;
  onRemove?: (value: string) => void;
};

export type OptionComponentProps = {
  id: string;
  label: string;
};

const defaultOptions: Option[] = [];

type Props = {
  onChange: (id: SelectValue) => void;
  options?: Option[];
  /** Override the default value component. */
  ValueComponent?: ComponentType<ValueComponentProps>;
  /** Override the default option component. */
  OptionComponent?: ComponentType<OptionComponentProps>;
  onCreateNew?: () => void;
  /** Callback when dropdown opens or closes */
  onOpenChange?: (open: boolean) => void;
} & ({ value: string[] } | { value: string });

/**
 * Select component with support for single and multi-select.
 * The single vs multi-select is determined by the type of the `value` prop.
 * If `value` is an array, it is a multi-select.
 * If `value` is a string, it is a single-select.
 */
export const Select = ({
  options = defaultOptions,
  onChange,
  value,
  ValueComponent,
  OptionComponent = Option,
  onCreateNew,
  onOpenChange: onOpenChangeProp,
}: Props) => {
  const [reference, setReference] = useState<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [search, setSearch] = useState<string>("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isMultiSelect = Array.isArray(value);

  const SelectedValueComponent = useMemo(() => {
    if (ValueComponent) {
      return ValueComponent;
    } else {
      return isMultiSelect ? TagValue : Value;
    }
  }, [isMultiSelect, ValueComponent]);

  const filteredOptions = useMemo(() => {
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(search?.toLowerCase() ?? "") &&
        (isMultiSelect ? !value.includes(option.id) : value !== option.id),
    );
  }, [options, search, value, isMultiSelect]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      // Call the prop callback BEFORE updating state to allow parent to prepare
      onOpenChangeProp?.(open);
      setIsOpen(open);
      if (!open) {
        setSearch("");
      }
    },
    [onOpenChangeProp],
  );

  const handleToggleOpen = useCallback(() => {
    const newOpenState = !isOpen;
    // Call the callback before toggling to allow parent to clear value synchronously
    onOpenChangeProp?.(newOpenState);
    setIsOpen(newOpenState);

    // Focus the input when opening
    if (newOpenState && inputRef.current) {
      // Use setTimeout to ensure focus happens after render
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [isOpen, onOpenChangeProp]);

  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom-start",
    open: isOpen,
    onOpenChange: handleOpenChange,
    whileElementsMounted: autoUpdate,
    elements: {
      reference,
    },
    middleware: [
      flip({
        padding: 12,
      }),
      offset({ mainAxis: 12 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });

  const elementsRef = useRef<Array<HTMLElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);

  const handleSelect = useCallback(
    (id: string, index: number | null) => {
      const newValues = isMultiSelect ? [...value, id] : id;
      onChange?.(newValues);
      setSelectedIndex(index);
      setIsOpen(false);
      setSearch("");
    },
    [isMultiSelect, onChange, value],
  );

  const handleRemove = useCallback(
    (id: string) => {
      if (isMultiSelect) {
        const newValues = value.filter((v) => v !== id);
        onChange?.(newValues);
      }
    },
    [isMultiSelect, onChange, value],
  );

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    // Prevent space key from being captured by list navigation when typing
    if (event.key === " ") {
      event.stopPropagation();
    }
  }, []);

  const listNavigation = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    onNavigate: setActiveIndex,
    focusItemOnHover: false,
  });

  const focus = useFocus(context);
  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [listNavigation, focus, dismiss, click],
  );

  const handleKeyDownCapture = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !search) {
      if (isMultiSelect) {
        const newValues = value.slice(0, -1);
        onChange?.(newValues);
      } else {
        onChange?.("");
      }
    }
  };

  const handleCreateNew = () => {
    setIsOpen(false);
    setSearch("");
    onCreateNew?.();
  };

  // Combine refs for the input element
  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      refs.setReference(node);
    },
    [refs],
  );

  const dropdownContext = useMemo(
    () => ({
      activeIndex,
      selectedIndex,
      getItemProps,
      handleSelect,
    }),
    [activeIndex, selectedIndex, getItemProps, handleSelect],
  );

  return (
    <>
      <Box stretch ref={(el) => setReference(el)}>
        <InputBox
          wrap={isMultiSelect}
          leftOnClick={handleToggleOpen}
          rightOnClick={handleToggleOpen}
          leftSlot={
            (isMultiSelect && value && value.length > 0) || (!isMultiSelect && !!value) ? (
              <>
                {isMultiSelect &&
                  value?.map((v) => (
                    <SelectedValueComponent
                      key={v}
                      options={options}
                      value={v}
                      onRemove={handleRemove}
                    />
                  ))}
                {!isMultiSelect && !!value && (
                  <SelectedValueComponent options={options} value={value} />
                )}
              </>
            ) : undefined
          }
          rightSlot={<Icon size="xs" icon="faChevronDown" />}
        >
          <Input
            tabIndex={0}
            role="combobox"
            placeholder={isEmpty(value) ? "Search..." : ""}
            {...getReferenceProps()}
            onKeyDown={handleKeyDown}
            onKeyDownCapture={handleKeyDownCapture}
            ref={setInputRef}
            onChange={handleSearch}
            value={search}
            // This ensures that the input doesn't preserve min-width on
            // small screens and wraps correctly when there isn't enough space.
            size={1}
          />
        </InputBox>
      </Box>

      <FloatingPortal>
        <SelectContext.Provider value={dropdownContext}>
          {isOpen && (
            <FloatingFocusManager
              context={context}
              modal={false}
              order={["reference", "floating"]}
              returnFocus={isMultiSelect}
            >
              <div
                ref={refs.setFloating}
                className={classes.floating}
                style={floatingStyles}
                {...getFloatingProps()}
              >
                <Card>
                  <ScrollBox stretch>
                    <div className={classes.list}>
                      <FloatingList
                        elementsRef={elementsRef}
                        labelsRef={labelsRef}
                      >
                        {filteredOptions.map((option) => (
                          <OptionContainer
                            key={option.id}
                            {...option}
                            OptionComponent={OptionComponent}
                          />
                        ))}
                        {filteredOptions.length === 0 && (
                          <Box padding="12px">
                            <Text color="textSecondary">No results found</Text>
                          </Box>
                        )}
                      </FloatingList>
                    </div>
                  </ScrollBox>
                  {onCreateNew && (
                    <Box padding="12px">
                      <Box direction="column" gap={8}>
                        <Text color="textSecondary">
                          Not what you're looking for?
                        </Text>
                        <Button
                          onClick={handleCreateNew}
                          size="sm"
                          variant="outlined"
                          icon="faPlus"
                        >
                          Create new
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Card>
              </div>
            </FloatingFocusManager>
          )}
        </SelectContext.Provider>
      </FloatingPortal>
    </>
  );
};

Select.Option = Option;
Select.TagValue = TagValue;
Select.Value = Value;
